import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, ExpenseClaim } from '../../api/finance';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', paid: 'Paid' };

export function ExpenseClaimsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const claimsQuery = useQuery({ queryKey: ['finance-expense-claims'], queryFn: () => financeApi.listExpenseClaims() });
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts(), enabled: showForm });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears() });
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<number | null>(null);
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', selectedFiscalYearId], queryFn: () => financeApi.getFiscalYear(selectedFiscalYearId!), enabled: !!selectedFiscalYearId });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-expense-claims'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createExpenseClaim({
      expenseDate: v.expenseDate, description: v.description, amount: Number(v.amount), expenseAccountId: Number(v.expenseAccountId),
      projectId: v.projectId ? Number(v.projectId) : undefined,
    }),
    onSuccess: (result) => { showSuccess(`Expense claim created - ${result.data.claimReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create expense claim.'),
  });

  const submitMutation = useMutation({ mutationFn: (id: number) => financeApi.submitExpenseClaim(id), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  const [approvingId, setApprovingId] = useState<number | null>(null);
  const approveMutation = useMutation({
    mutationFn: ({ id, periodId }: { id: number; periodId: number }) => financeApi.approveExpenseClaim(id, { fiscalYearId: selectedFiscalYearId!, accountingPeriodId: periodId }),
    onSuccess: (result) => { showSuccess(`Approved - journal ${result.data.journalReference}, payable ${result.data.payableReference}.`); invalidate(); setApprovingId(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to approve - check Finance Settings has the Accounts Payable account configured.'),
  });
  const rejectMutation = useMutation({ mutationFn: ({ id, comment }: { id: number; comment: string }) => financeApi.rejectExpenseClaim(id, comment), onSuccess: () => { showSuccess('Rejected.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Expense Claims</h1>
        <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Expense Claim'}</button>
      </div>

      {showForm && (
        <section className="card">
          <h2>New Expense Claim</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Expense Date<input type="date" {...register('expenseDate', { required: true })} /></label>
            <label>Description<input {...register('description', { required: true })} /></label>
            <label>Amount<input type="number" step="0.01" {...register('amount', { required: true })} /></label>
            <label>
              Expense Account
              <select {...register('expenseAccountId', { required: true })}>
                <option value="">- select -</option>
                {accountsQuery.data?.data.filter((a) => a.posting_allowed && a.account_type === 'expense').map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>
            <div className="form-actions"><button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft'}</button></div>
          </form>
        </section>
      )}

      {approvingId !== null && (
        <section className="card">
          <h2>Approve Expense Claim</h2>
          <label>
            Fiscal Year
            <select onChange={(e) => setSelectedFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => { if (e.target.value) approveMutation.mutate({ id: approvingId, periodId: Number(e.target.value) }); }}>
              <option value="">- select to approve -</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.period_label}</option>)}
            </select>
          </label>
          <button type="button" className="link-button" onClick={() => setApprovingId(null)}>Cancel</button>
        </section>
      )}

      <section className="card">
        {claimsQuery.data && (
          <DataTable<ExpenseClaim>
            rows={claimsQuery.data.data}
            rowKey={(c) => c.id}
            emptyMessage="No expense claims yet."
            columns={[
              { header: 'Reference', render: (c) => c.claim_reference },
              { header: 'Claimant', render: (c) => c.claimant_name ?? '-' },
              { header: 'Date', render: (c) => c.expense_date },
              { header: 'Description', render: (c) => c.description },
              { header: 'Amount', render: (c) => Number(c.amount).toLocaleString() },
              { header: 'Account', render: (c) => c.account_code ? `${c.account_code} - ${c.account_name}` : '-' },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} label={STATUS_LABELS[c.status]} /> },
              {
                header: '',
                render: (c) => (
                  <span className="row-actions">
                    {c.status === 'draft' && <button className="link-button" onClick={() => submitMutation.mutate(c.id)}>Submit</button>}
                    {c.status === 'submitted' && <button className="link-button" onClick={() => setApprovingId(c.id)}>Approve</button>}
                    {c.status === 'submitted' && <button className="link-button" onClick={() => { const cm = window.prompt('Rejection reason:'); if (cm) rejectMutation.mutate({ id: c.id, comment: cm }); }}>Reject</button>}
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>
    </div>
  );
}
